import { DateRangeMode } from '../const/DateRangeMode';
import { endOfWeek, startOfWeek } from 'date-fns';
import type { IFilter } from '../models/IFilter.ts';
import { DateFieldNames } from '../services/filterService';
import { linkService } from '../services/linkService';
import { parseDateTimeForUrl } from './dateUtils';
import { lowercaseFirstChar } from './stringUtils';
import type { DetailRoute } from '../api/summaryApi';

type OpenEmployeeRelationStatsPageProps = {
  employee: number | string;
  dateRangeMode: string;
  dateFilters: IFilter[];
  clientRelationStatsRoute?: DetailRoute;
  bcId: number;
  date?: Date;
};

export const buildEmployeeRelationStatsUrl = ({
  employee,
  dateFilters,
  date,
  bcId,
  clientRelationStatsRoute,
  dateRangeMode,
}: OpenEmployeeRelationStatsPageProps): string | null => {
  if (!clientRelationStatsRoute?.url || !clientRelationStatsRoute.detailFormKey) {
    return null;
  }

  const employeeId = Number(employee);

  if (!Number.isInteger(employeeId) || employeeId <= 0) {
    return null;
  }

  const params: { parametr: string; value: number | string }[] = [
    {
      parametr: 'detailFormKey',
      value: clientRelationStatsRoute.detailFormKey,
    },
    {
      parametr: DateFieldNames.BcId,
      value: bcId,
    },
  ];

  if (dateRangeMode === DateRangeMode.Week) {
    const rangeFilters = dateFilters.filter((f) => f.DataField !== DateFieldNames.Employee);

    rangeFilters.forEach((filter) =>
      params.push({
        parametr: lowercaseFirstChar(filter.DataField),
        value: parseDateTimeForUrl(new Date(filter.Value)),
      })
    );

    if (date) {
      params.push({
        parametr: DateFieldNames.DetailedData,
        value: parseDateTimeForUrl(new Date(date)),
      });
    }
  } else if (date) {
    const clickedMonth = date.getMonth();
    const clickedYear = date.getFullYear();

    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 });

    const startDate =
      weekStart.getMonth() !== clickedMonth ? new Date(clickedYear, clickedMonth, 1) : weekStart;

    const endDate =
      weekEnd.getMonth() !== clickedMonth ? new Date(clickedYear, clickedMonth + 1, 0) : weekEnd;

    params.push(
      { parametr: DateFieldNames.StartDate, value: parseDateTimeForUrl(startDate) },
      { parametr: DateFieldNames.EndDate, value: parseDateTimeForUrl(endDate) }
    );
  }

  const baseUrl = clientRelationStatsRoute.url.replace(/\/+$/, '');
  const entityPath = String(employeeId);
  const search = new URLSearchParams(
    params.map(({ parametr, value }) => [parametr, String(value)])
  );

  return `${baseUrl}/${entityPath}?${search.toString()}`;
};

export const openEmployeeRelationStatsPage = (props: OpenEmployeeRelationStatsPageProps) => {
  const url = buildEmployeeRelationStatsUrl(props);
  if (url) {
    linkService.openLink(url);
  }
};
